import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { aboutService } from '../../services/aboutService';
import { contactService } from '../../services/contactService';
import { heroService } from '../../services/heroService';
import { whatWeDoService } from '../../services/whatWeDoService';
import { howItWorksService } from '../../services/howItWorksService';
import { valuationFormService } from '../../services/valuationFormService';
import { footerService } from '../../services/footerService';
import { godrejShettigereService } from '../../services/godrejshettigereService';
import { godrejBarcaMsrService } from '../../services/godrejBacraMsrService';
import { headerService } from '../../services/headerService';
import { whyChooseUsService } from '../../services/whyChooseUsService'; 
import { Edit, LayoutDashboard, FileText, Settings, Sparkles, Sliders, DollarSign, PenSquare, ChevronLeft, TextCursorInput, Globe, ImageIcon } from 'lucide-react'; // Import ImageIcon
import HeroContentManager from './HeroContentManager';
import AboutContentManager from './AboutContentManager';
import ContactContentManager from './ContactContentManager';
import GodrejShettigereContentManager from './GodrejShettigerecontentManager';
import GodrejBarcaMsrContentManager from './GodrejBacraMsrContentManager'
import WhatWeDoContentManager from './WhatWeDoContentManager';
import HowItWorksContentManager from './HowItWorksContentManager';
import ValuationFormContentManager from './ValuationFormContentManager';
import FooterContentManager from './FooterContentManager';
import HeaderContentManager from './HeaderContentManager';
import WhyChooseUsContentManager from './WhyChooseUsContentManager'; 

const ContentManager = () => {
  const { showError } = useError();

  const [activeTab, setActiveTab] = useState('pages');
  const [editingHomePage, setEditingHomePage] = useState(false);
  const [editingAbout, setEditingAbout] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [editingGodrejShettigere, setEditingGodrejShettigere] = useState(false);
  const [editingGodrejBarcaMsr, setEditingGodrejBarcaMsr] = useState(false);
  const [editingHero, setEditingHero] = useState(false);
  const [editingWhatWeDo, setEditingWhatWeDo] = useState(false);
  const [editingHowItWorks, setEditingHowItWorks] = useState(false);
  const [editingValuationForm, setEditingValuationForm] = useState(false);
  const [editingFooter, setEditingFooter] = useState(false);
  const [editingHeader, setEditingHeader] = useState(false);
  const [editingWhyChooseUs, setEditingWhyChooseUs] = useState(false); 

  const [pages, setPages] = useState([
    { id: 1, title: 'Home', lastUpdated: null, editable: true, icon: <LayoutDashboard className="w-5 h-5" /> },
    { id: 2, title: 'About', lastUpdated: null, editable: true, icon: <FileText className="w-5 h-5" /> },
    { id: 3, title: 'Contact', lastUpdated: null, editable: true, icon: <Settings className="w-5 h-5" /> },
    { id: 4, title: 'Godrej Shettigere', lastUpdated: null, editable: true, icon: <FileText className="w-5 h-5" /> },
    { id: 5, title: 'Godrej MSR City', lastUpdated: null, editable: true, icon: <FileText className="w-5 h-5" /> },
  ]);

  const [sections, setSections] = useState([
   
    { id: 1, title: 'Footer', component: 'FooterContentManager', icon: <FileText className="w-5 h-5 text-purple-500" />, lastUpdated: null },
    { id: 2, title: 'Header', component: 'HeaderContentManager', icon: <TextCursorInput className="w-5 h-5 text-blue-500" />, lastUpdated: null },
    { id: 2, title: 'Global Sections',  icon: <Globe className="w-5 h-5 text-blue-500" />, lastUpdated: null },

  ]);

  const homeSections = [
    { id: 1, name: 'Hero Section', component: 'HeroContentManager', icon: <Sparkles className="w-5 h-5 text-indigo-500" /> },
    { id: 2, name: 'What We Do', component: 'WhatWeDoContentManager', icon: <Sliders className="w-5 h-5 text-green-500" /> },
    { id: 3, name: 'How It Works', component: 'HowItWorksContentManager', icon: <DollarSign className="w-5 h-5 text-yellow-500" /> },
    { id: 4, name: 'Valuation Form', component: 'ValuationFormContentManager', icon: <PenSquare className="w-5 h-5 text-red-500" /> },
    { id: 5, name: 'Why Choose Us', component: 'WhyChooseUsContentManager', icon: <ImageIcon className="w-5 h-5 text-teal-500" /> }, 
    { id: 6, name: 'Footer', component: 'FooterContentManager', icon: <FileText className="w-5 h-5 text-purple-500" /> },
  ];

  useEffect(() => {
    const fetchInitialPageDates = async () => {
      try {
        const heroData = await heroService.getHeroSlides();
        const whatWeDoData = await whatWeDoService.getWhatWeDoContent();
        const howItWorksData = await howItWorksService.getHowItWorksContent();
        const valuationFormData = await valuationFormService.getValuationFormContent();
        const whyChooseUsData = await whyChooseUsService.getWhyChooseUsContent(); 
        const footerData = await footerService.getFooterContent();
        const headerData = await headerService.getHeaderContent();

        const homeUpdateDates = [
          ...heroData.map(slide => new Date(slide.updated_at || 0).getTime()),
          new Date(whatWeDoData?.lastUpdated || 0).getTime(),
          new Date(howItWorksData?.lastUpdated || 0).getTime(),
          new Date(valuationFormData?.lastUpdated || 0).getTime(),
          new Date(whyChooseUsData?.lastUpdated || 0).getTime(), 
        ];

        const homeLastUpdated = homeUpdateDates.length > 0
          ? new Date(Math.max(...homeUpdateDates)).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;

        const aboutData = await aboutService.getAboutContent();
        const aboutLastUpdated = aboutData && aboutData.updated_at
          ? new Date(aboutData.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;

        const contactData = await contactService.getContactContent();
        const contactLastUpdated = contactData && contactData.updated_at
          ? new Date(contactData.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;

        const godrejShettigereData = await godrejShettigereService.getGodrejContent();
        const godrejShettigereLastUpdated = godrejShettigereData && godrejShettigereData.lastUpdated
          ? new Date(godrejShettigereData.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;

        const godrejBarcaMsrData = await godrejBarcaMsrService.getGodrejContent();
        const godrejBarcaMsrLastUpdated = godrejBarcaMsrData && godrejBarcaMsrData.lastUpdated
          ? new Date(godrejBarcaMsrData.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;
        
        const footerLastUpdated = footerData && footerData.lastUpdated
          ? new Date(footerData.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;

        const headerLastUpdated = headerData && headerData.lastUpdated
          ? new Date(headerData.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;

        setPages(prevPages => prevPages.map(page => {
          if (page.title === 'Home') return { ...page, lastUpdated: homeLastUpdated };
          if (page.title === 'About') return { ...page, lastUpdated: aboutLastUpdated };
          if (page.title === 'Contact') return { ...page, lastUpdated: contactLastUpdated };
          if (page.title === 'Godrej Shettigere') return { ...page, lastUpdated: godrejShettigereLastUpdated };
          if (page.title === 'Godrej MSR City') return { ...page, lastUpdated: godrejBarcaMsrLastUpdated };
          return page;
        }));

        setSections(prevSections => prevSections.map(section => {
          if (section.title === 'Header') return { ...section, lastUpdated: headerLastUpdated };
          if (section.title === 'About') return { ...section, lastUpdated: aboutLastUpdated };
          if (section.title === 'Contact') return { ...section, lastUpdated: contactLastUpdated };
          if (section.title === 'Footer') return { ...section, lastUpdated: footerLastUpdated };
          return section;
        }));
      } catch (error) {
        console.error("Failed to fetch initial page lastUpdated dates:", error);
        showError('Failed to load page last updated dates.', 'error');
      }
    };
    fetchInitialPageDates();
  }, [showError]);

  const updatePageLastUpdated = async (pageTitle) => {
    let latestUpdateDate = null;
    try {
      if (pageTitle === 'Home') {
        const heroData = await heroService.getHeroSlides();
        const whatWeDoData = await whatWeDoService.getWhatWeDoContent();
        const howItWorksData = await howItWorksService.getHowItWorksContent();
        const valuationFormData = await valuationFormService.getValuationFormContent();
        const whyChooseUsData = await whyChooseUsService.getWhyChooseUsContent(); 
        const footerData = await footerService.getFooterContent();
        
        const homeUpdateDates = [
          ...heroData.map(slide => new Date(slide.updated_at || 0).getTime()),
          new Date(whatWeDoData?.lastUpdated || 0).getTime(),
          new Date(howItWorksData?.lastUpdated || 0).getTime(),
          new Date(valuationFormData?.lastUpdated || 0).getTime(),
          new Date(whyChooseUsData?.lastUpdated || 0).getTime(), 
          new Date(footerData?.lastUpdated || 0).getTime(),
        ];
        
        latestUpdateDate = homeUpdateDates.length > 0
          ? new Date(Math.max(...homeUpdateDates)).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;
      } else if (pageTitle === 'About') {
        const aboutData = await aboutService.getAboutContent();
        latestUpdateDate = aboutData && aboutData.updated_at
          ? new Date(aboutData.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;
      } else if (pageTitle === 'Contact') {
        const contactData = await contactService.getContactContent();
        latestUpdateDate = contactData && contactData.updated_at
          ? new Date(contactData.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;
      } else if (pageTitle === 'Godrej Shettigere') {
        const godrejData = await godrejShettigereService.getGodrejContent();
        latestUpdateDate = godrejData && godrejData.lastUpdated
          ? new Date(godrejData.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;
      } else if (pageTitle === 'Godrej MSR City') {
        const godrejData = await godrejBarcaMsrService.getGodrejContent();
        latestUpdateDate = godrejData && godrejData.lastUpdated
          ? new Date(godrejData.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;
      } else if (pageTitle === 'Header') {
        const headerData = await headerService.getHeaderContent();
        latestUpdateDate = headerData && headerData.lastUpdated
          ? new Date(headerData.lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
          : null;
      }
    } catch (error) {
      console.error(`Failed to fetch latest update date for ${pageTitle}:`, error);
      showError(`Failed to update last updated date for ${pageTitle}.`, 'error');
    }

    setPages(prevPages =>
      prevPages.map(page =>
        page.title === pageTitle
          ? { ...page, lastUpdated: latestUpdateDate }
          : page
      )
    );
  };

  const resetEditingStates = () => {
    setEditingHomePage(false);
    setEditingAbout(false);
    setEditingContact(false);
    setEditingGodrejShettigere(false);
    setEditingGodrejBarcaMsr(false);
    setEditingHero(false);
    setEditingWhatWeDo(false);
    setEditingHowItWorks(false);
    setEditingValuationForm(false);
    setEditingFooter(false);
    setEditingHeader(false);
    setEditingWhyChooseUs(false); 
  };

  const handleEditPage = (page) => {
    resetEditingStates();
    if (page.title === 'Home') {
      setEditingHomePage(true);
    } else if (page.title === 'About') {
      setEditingAbout(true);
    } else if (page.title === 'Contact') {
      setEditingContact(true);
    } else if (page.title === 'Godrej Shettigere') {
      setEditingGodrejShettigere(true);
    } else if (page.title === 'Godrej MSR City') {
      setEditingGodrejBarcaMsr(true);
    } else {
      showError('Content editing for this page will be implemented soon', 'info');
    }
  };
  
  const handleEditHomeSection = (sectionName) => {
    resetEditingStates();
    switch (sectionName) {
      case 'Hero Section':
        setEditingHero(true);
        break;
      case 'What We Do':
        setEditingWhatWeDo(true);
        break;
      case 'How It Works':
        setEditingHowItWorks(true);
        break;
      case 'Valuation Form':
        setEditingValuationForm(true);
        break;
      case 'Why Choose Us': 
        setEditingWhyChooseUs(true);
        break;
      case 'Footer':
        setEditingFooter(true);
        break;
      default:
        showError('Content editing for this section will be implemented soon', 'info');
        break;
    }
  };

  const handleEditSection = (section) => {
    resetEditingStates();
    switch (section.title) {
      case 'About':
        setEditingAbout(true);
        break;
      case 'Contact':
        setEditingContact(true);
        break;
      case 'Footer':
        setEditingFooter(true);
        break;
      case 'Header':
        setEditingHeader(true);
        break;
      default:
        showError('Content editing for this section will be implemented soon', 'info');
        break;
    }
  };

  const renderContent = () => {
    if (editingAbout) return <AboutContentManager setEditingAbout={setEditingAbout} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingContact) return <ContactContentManager setEditingContact={setEditingContact} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingGodrejShettigere) return <GodrejShettigereContentManager setEditingGodrejShettigere={setEditingGodrejShettigere} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingGodrejBarcaMsr) return <GodrejBarcaMsrContentManager setEditingGodrejBarcaMsr={setEditingGodrejBarcaMsr} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingHeader) return <HeaderContentManager setEditingHeader={setEditingHeader} updatePageLastUpdated={updatePageLastUpdated} />;
    
    if (editingHero) return <HeroContentManager setEditingHero={setEditingHero} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingWhatWeDo) return <WhatWeDoContentManager setEditingWhatWeDo={setEditingWhatWeDo} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingHowItWorks) return <HowItWorksContentManager setEditingHowItWorks={setEditingHowItWorks} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingValuationForm) return <ValuationFormContentManager setEditingValuationForm={setEditingValuationForm} updatePageLastUpdated={updatePageLastUpdated} />;
    if (editingWhyChooseUs) return <WhyChooseUsContentManager setEditingWhyChooseUs={setEditingWhyChooseUs} updatePageLastUpdated={updatePageLastUpdated} />; 
    if (editingFooter) return <FooterContentManager setEditingFooter={setEditingFooter} updatePageLastUpdated={updatePageLastUpdated} />;

    if (editingHomePage) {
      return (
        <div className="mt-8 bg-white dark:bg-gray-800 p-1 md:p-6 rounded-xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-700 animate-fade-in">
          <div className="flex items-center space-x-4 mb-6">
            <button onClick={() => resetEditingStates()} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Home Page Sections</h3>
          </div>
          <p className="text-md text-gray-600 dark:text-gray-400 mb-6">
            Select a section to edit its content.
          </p>
          <div className="space-y-4">
            {homeSections.map((section) => (
              <div key={section.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                <div className="flex items-center">
                  {section.icon}
                  <p className="text-base font-semibold text-gray-900 dark:text-white ml-4">{section.name}</p>
                </div>
                <button
                  onClick={() => handleEditHomeSection(section.name)}
                  className="flex items-center px-4 py-2 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-accentdark:hover:bg-accent transition duration-200 text-sm font-medium"
                >
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <div className="mt-8 bg-white dark:bg-gray-800 p-1 md:p-6 rounded-xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-700 animate-fade-in">
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('pages')}
              className={`${
                activeTab === 'pages'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-500'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              Pages
            </button>
            <button
              onClick={() => setActiveTab('sections')}
              className={`${
                activeTab === 'sections'
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-500'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              Sections
            </button>
          </nav>
        </div>

        {activeTab === 'pages' ? (
          <>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Manage Pages</h3>
            <div className="space-y-4">
              {pages.map((page) => (
                <div key={page.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                  <div className="flex items-center">
                    {page.icon}
                    <div className="ml-4">
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{page.title}</p>
                      {page.lastUpdated && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Last Updated: {page.lastUpdated}</p>}
                    </div>
                  </div>
                  {page.editable && (
                    <button
                      onClick={() => handleEditPage(page)}
                      className="flex items-center px-4 py-2 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 transition duration-200 text-sm font-medium"
                    >
                      <Edit className="w-4 h-4 mr-2" /> Edit
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Manage Sections</h3>
            <div className="space-y-4">
              {sections.map((section) => (
                <div key={section.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                  <div className="flex items-center">
                    {section.icon}
                    <div className="ml-4">
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{section.title}</p>
                      {section.lastUpdated && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Last Updated: {section.lastUpdated}</p>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleEditSection(section)}
                    className="flex items-center px-4 py-2 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 transition duration-200 text-sm font-medium"
                  >
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-6">Content Management</h1>
      <p className="text-lg text-gray-600 dark:text-gray-400">
        Easily manage and update the content of your website pages and sections.
      </p>
      {renderContent()}
    </div>
  );
};

export default ContentManager;